import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  useState,
} from 'react';
import { useCreateStore, Store } from 'hooks/createStore';
import { useLevels } from 'store/level';
import { findDocumentationOnLevelsAndSites } from 'shared/domain/documentation/findDocumentationOnLevels';
import { DocumentationBaseIds } from 'shared/types/documentation';
import { useProjectId } from 'store/project';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';

type File = DocumentationBaseIds & { loading: boolean };

interface IssueOverlayControlsContextType {
  issuesVisibleStore: Store<boolean>;
  setFile: (file: File | undefined) => void;
  toggleVisible: () => void;
  toggleFiltersVisible: () => void;
  setFiltersVisible: (visible: boolean) => void;
  disabled: boolean;
  filtersDisabled: boolean;
  filtersDisabledReason: string;
  filtersVisibleStore: Store<boolean>;
}
const IssueOverlayControlsContext = createContext<
  IssueOverlayControlsContextType | undefined
>(undefined);

function getFileIdentity(file: File | undefined): string | undefined {
  if (!file) {
    return undefined;
  }

  return `${file.documentationId ?? ''}:${file.versionId ?? ''}`;
}

function useOverlayAvailability(
  file: File | undefined,
  issuesVisible: boolean
): {
  disabled: boolean;
  filtersDisabled: boolean;
  filtersDisabledReason: string;
} {
  const { loading: loadingLevels, items: levels } = useLevels();
  const documentationId = file?.documentationId;
  const versionId = file?.versionId;

  const { levelsValue } = useMemo(
    () =>
      findDocumentationOnLevelsAndSites(
        levels,
        [],
        documentationId,
        versionId
      ),
    [levels, documentationId, versionId]
  );

  const isUsedAsLevel = levelsValue.length > 0;
  const disabled =
    !file || !isUsedAsLevel || loadingLevels || Boolean(file?.loading);

  return {
    disabled,
    filtersDisabled: disabled || !issuesVisible,
    filtersDisabledReason: disabled
      ? 'documentation_file_not_used_as_map'
      : 'documentation_filtering_unavailable',
  };
}

function useCloseFiltersOnFileChange(
  fileIdentity: string | undefined,
  filtersVisibleStore: Store<boolean>
): void {
  const previousFileIdentityRef = useRef<string | undefined>(fileIdentity);

  useEffect(() => {
    if (previousFileIdentityRef.current === fileIdentity) {
      return;
    }

    previousFileIdentityRef.current = fileIdentity;

    if (filtersVisibleStore.get()) {
      filtersVisibleStore.set(false);
    }
  }, [fileIdentity, filtersVisibleStore]);
}

const WithIssueOverlayControls: FC<
  PropsWithChildren<{
    issuesVisibleStore: Store<boolean>;
    filtersVisibleStore: Store<boolean>;
  }>
> = ({ children, issuesVisibleStore, filtersVisibleStore }) => {
  const projectId = useProjectId();
  const [file, setFile] = useState<File | undefined>(undefined);
  const issuesVisible = useSyncExternalStore(
    issuesVisibleStore.subscribe,
    issuesVisibleStore.get
  );
  const filtersVisible = useSyncExternalStore(
    filtersVisibleStore.subscribe,
    filtersVisibleStore.get
  );
  const fileIdentity = useMemo(() => getFileIdentity(file), [file]);

  const {
    disabled,
    filtersDisabled,
    filtersDisabledReason,
  } = useOverlayAvailability(file, issuesVisible);

  useCloseFiltersOnFileChange(fileIdentity, filtersVisibleStore);

  const toggleVisible = useCallback(() => {
    if (disabled) {
      return;
    }
    const next = !issuesVisibleStore.get();
    LocalStorageManager.setDocumentationIssuesVisible(projectId, next);
    issuesVisibleStore.set(next);
  }, [disabled, issuesVisibleStore, projectId]);

  const toggleFiltersVisible = useCallback(() => {
    if (disabled || !issuesVisible) {
      return;
    }

    const next = !filtersVisibleStore.get();
    filtersVisibleStore.set(next);
  }, [disabled, issuesVisible, filtersVisibleStore]);

  const setFiltersVisible = useCallback(
    (visible: boolean) => {
      if (visible && (disabled || !issuesVisible)) {
        return;
      }

      filtersVisibleStore.set(visible);
    },
    [disabled, issuesVisible, filtersVisibleStore]
  );

  const ctx: IssueOverlayControlsContextType = useMemo(() => {
    return {
      issuesVisibleStore,
      filtersVisibleStore,
      setFile,
      toggleVisible,
      toggleFiltersVisible,
      setFiltersVisible,
      disabled,
      filtersDisabled,
      filtersDisabledReason,
    };
  }, [
    issuesVisibleStore,
    toggleVisible,
    toggleFiltersVisible,
    setFiltersVisible,
    disabled,
    filtersDisabled,
    filtersDisabledReason,
    filtersVisibleStore,
  ]);

  return (
    <IssueOverlayControlsContext.Provider value={ctx}>
      {children}
    </IssueOverlayControlsContext.Provider>
  );
};

function useIssueOverlayControls(): IssueOverlayControlsContextType {
  const context = useContext(IssueOverlayControlsContext);
  if (context === undefined) {
    throw new Error(
      'useIssueOverlayControls must be used within an IssueOverlayControlsContextProvider'
    );
  }
  return context;
}

export { WithIssueOverlayControls, useIssueOverlayControls };

export function useCreateIssuesVisibleStore(): Store<boolean> {
  const projectId = useProjectId();
  const savedValue = LocalStorageManager.getDocumentationIssuesVisible(projectId);
  return useCreateStore(savedValue);
}
